import { formatTerritory } from "../mocks/va/mockData";
import { serializeLegacyPrescription } from "./prescriptionFormat";

export function formatTerritories(territories) {
  if (!Array.isArray(territories) || !territories.length) return "";
  return territories.map(formatTerritory).join(", ");
}

export function mapDistributorOption(d) {
  if (!d) return null;
  // Prefer hospital label from parse; fall back to territories for older payloads.
  const hospitalLabel =
    d.hospitalName ||
    (Array.isArray(d.hospitals) && d.hospitals[0]?.name) ||
    "";
  const territory =
    hospitalLabel ||
    formatTerritories(d.territories) ||
    (d.territory ? formatTerritory(d.territory) : "");
  const hospitalIdsFromArray = Array.isArray(d.hospitalIds)
    ? d.hospitalIds.map(String)
    : [];
  const hospitalIdsFromHospitals = Array.isArray(d.hospitals)
    ? d.hospitals.map((h) => String(h?.id || h?._id || "")).filter(Boolean)
    : [];
  const hospitalIds = [
    ...new Set([...hospitalIdsFromArray, ...hospitalIdsFromHospitals]),
  ];
  return {
    id: d.id,
    name: d.name,
    email: String(d.email || "").trim(),
    territory,
    territories: Array.isArray(d.territories)
      ? d.territories.map((code) => String(code || "").trim().toUpperCase()).filter(Boolean)
      : [],
    hospitalId: d.hospitalId || null,
    hospitalName: d.hospitalName || hospitalLabel || null,
    hospitalIds,
    coversMatchedHospital:
      typeof d.coversMatchedHospital === "boolean"
        ? d.coversMatchedHospital
        : null,
    aiReason: d.aiReason,
    suggested: d.suggested,
  };
}

/** Flatten grouped hospital API / parse options into dropdown rows. */
export function flattenHospitalOptions(groupsOrList = []) {
  if (!Array.isArray(groupsOrList) || !groupsOrList.length) return [];

  // Already flat list of hospitals
  if (groupsOrList[0]?.id && groupsOrList[0]?.name && !groupsOrList[0]?.hospitals) {
    return groupsOrList.map((h) => ({
      id: String(h.id),
      name: h.name || "",
      city: h.city || "",
      stateCode: h.stateCode || "",
      stateName: h.stateName || "",
      label: h.city
        ? `${h.name} (${h.city}${h.stateCode ? `, ${h.stateCode}` : ""})`
        : h.name,
    }));
  }

  const rows = [];
  for (const group of groupsOrList) {
    const hospitals = Array.isArray(group.hospitals) ? group.hospitals : [];
    for (const hospital of hospitals) {
      rows.push({
        id: String(hospital.id),
        name: hospital.name || "",
        city: hospital.city || "",
        stateCode: group.stateCode || hospital.stateCode || "",
        stateName: group.stateName || hospital.stateName || "",
        label: hospital.city
          ? `${hospital.name} (${hospital.city}${
              group.stateCode ? `, ${group.stateCode}` : ""
            })`
          : hospital.name,
      });
    }
  }
  return rows;
}

/** Distributors that cover a given hospital id (legacy hospitalIds coverage). */
export function filterDistributorsByHospital(options = [], hospitalId) {
  const id = String(hospitalId || "").trim();
  if (!id) return options;
  return options.filter((opt) => {
    const ids = Array.isArray(opt.hospitalIds) ? opt.hospitalIds.map(String) : [];
    if (ids.length === 0) return false;
    return ids.includes(id);
  });
}

/** Distributors whose territories include this US state code. */
export function filterDistributorsByState(options = [], stateCode) {
  const code = String(stateCode || "").trim().toUpperCase();
  if (!code) return options;
  return options.filter((opt) => {
    const territories = Array.isArray(opt.territories)
      ? opt.territories.map((t) => String(t || "").trim().toUpperCase())
      : [];
    if (territories.length === 0) return false;
    return territories.includes(code);
  });
}

/** Display label used in Order From / distributor dropdowns. */
export function formatDistributorLabel(opt) {
  if (!opt) return "";
  const name = opt.name || opt.label || String(opt.id || "");
  const territory = opt.territory || "";
  return territory ? `${name} - ${territory}` : name;
}

const PO_NUMBER_KEYS = [
  "poNumber",
  "po_number",
  "PONumber",
  "PoNumber",
  "poNo",
  "po",
];

/** Pick a PO number from invoiceMeta / parse payload regardless of casing. */
export function getPoNumber(source) {
  if (!source || typeof source !== "object") return "";
  for (const key of PO_NUMBER_KEYS) {
    const value = source[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
}

/** Shopify order note text for a PO number. */
export function formatPoNumberNote(poNumber) {
  const value = String(poNumber || "").trim();
  if (!value) return "";
  return /p\.?\s*o\.?/i.test(value) ? value : `P.O.# ${value}`;
}

/** Normalize POST /va/invoices/parse response for review UI. */
export function normalizeParseResult(response) {
  let payload = response?.data ?? response?.item ?? response;
  if (payload?.data && payload?.parseId == null) {
    payload = payload.data;
  }
  if (!payload?.parseId) return null;

  const distributorOptions = (
    payload.distributorOptions ??
    payload.distributors ??
    []
  )
    .map(mapDistributorOption)
    .filter(Boolean);

  const distributor = mapDistributorOption(payload.distributor);

  const hospitalOptions = Array.isArray(payload.hospitalOptions)
    ? flattenHospitalOptions(payload.hospitalOptions)
    : [];
  const hospital = payload.hospital
    ? flattenHospitalOptions([payload.hospital])[0] || null
    : null;
  const suggestedHospitals = (
    Array.isArray(payload.suggestedHospitals)
      ? payload.suggestedHospitals
      : []
  )
    .map((h) => ({
      id: String(h.id),
      name: h.name || "",
      city: h.city || "",
      stateCode: h.stateCode || "",
      stateName: h.stateName || "",
      matchScore: Number(h.matchScore) || 0,
      suggested: true,
      label: h.city
        ? `${h.name} (${h.city}${h.stateCode ? `, ${h.stateCode}` : ""})`
        : h.name,
    }))
    .filter((h) => h.id);

  const suggestedDistributors = (
    Array.isArray(payload.suggestedDistributors)
      ? payload.suggestedDistributors
      : []
  )
    .map(mapDistributorOption)
    .filter(Boolean)
    .map((d) => ({ ...d, suggested: true }));

  const facilityName =
    payload.facilityName ||
    payload.delivery?.facilityName ||
    payload.delivery?.hospitalName ||
    "";

  const bundleProducts =
    payload.bundleProducts ??
    payload.bundle?.bundleProducts ??
    null;

  const rawInvoiceMeta = payload.invoiceMeta || payload.invoice_meta || {};
  const invoiceMeta = {
    ...rawInvoiceMeta,
    poNumber:
      getPoNumber(rawInvoiceMeta) || getPoNumber(payload),
    // Explicitly keep Patient name / Provider name sources for Create Order UI.
    veteran: String(
      rawInvoiceMeta.veteran || payload.veteran || ""
    ).trim(),
    provider: String(
      rawInvoiceMeta.provider || payload.provider || ""
    ).trim(),
  };

  const delivery = enrichDeliveryName(
    {
      ...(payload.delivery || {}),
      hospitalId:
        payload.delivery?.hospitalId ||
        hospital?.id ||
        null,
      hospitalName:
        payload.delivery?.hospitalName ||
        payload.delivery?.facilityName ||
        "",
      facilityName:
        payload.delivery?.facilityName ||
        payload.facilityName ||
        payload.delivery?.hospitalName ||
        "",
      audiologistName:
        payload.delivery?.audiologistName ||
        invoiceMeta.audiologistName ||
        "",
    },
    invoiceMeta.audiologistName
  );

  return {
    parseId: payload.parseId,
    bundle: {
      code: payload.bundle?.code || "CAP1",
      ...payload.bundle,
      bundleProducts,
    },
    bundleProducts,
    facilityName,
    hospital,
    hospitalOptions,
    suggestedHospitals,
    suggestedDistributors,
    hospitalMatch: payload.hospitalMatch || null,
    needsManualHospital: payload.needsManualHospital === true,
    canOverrideDistributorTerritory:
      payload.canOverrideDistributorTerritory === true,
    distributor,
    distributorOptions,
    prescription: payload.prescription || {
      od: {},
      os: {},
      pdType: null,
      fileUrl: null,
      fileName: null,
    },
    delivery,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
    aiMeta: payload.aiMeta || {},
    invoiceMeta,
  };
}

/**
 * Ensure Delivery "Name" is filled from ship-to audiologist lines like
 * "Dr. Atkins in Audiology - 673-I60434".
 * Also splits a combined "City, ST ZIP" blob into separate city / state / zip.
 */
export function enrichDeliveryName(delivery = {}, audiologistName = "") {
  const next = { ...(delivery || {}) };

  let city = String(next.city || "").trim();
  let state = String(next.state || "").trim().toUpperCase();
  let zip = String(next.zip || "").trim();

  if (city && (!state || !zip) && /\b[A-Za-z]{2}\s+\d{5}/.test(city)) {
    const parsed = parseCityStateZip(city);
    city = parsed.city || city;
    state = state || parsed.state;
    zip = zip || parsed.zip;
  }

  if ((!city || !state || !zip) && next.rawShipTo) {
    const lines = String(next.rawShipTo)
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const cityLine = [...lines]
      .reverse()
      .find((line) => /\b[A-Za-z]{2}\s+\d{5}(?:-\d{4})?\b/.test(line));
    if (cityLine) {
      const parsed = parseCityStateZip(cityLine);
      city = city || parsed.city;
      state = state || parsed.state;
      zip = zip || parsed.zip;
    }
  }

  next.city = city;
  next.state = state;
  next.zip = zip;

  const hasName = Boolean(
    String(next.firstName || "").trim() || String(next.lastName || "").trim()
  );
  if (hasName) return next;

  const person = extractPersonNameFromShipTo(audiologistName)
    || extractPersonNameFromShipTo(next.clinicName)
    || extractPersonNameFromShipTo(next.hospitalName);

  if (!person) return next;

  const parts = person.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    next.firstName = parts[0];
    next.lastName = "";
  } else {
    next.firstName = parts.slice(0, -1).join(" ");
    next.lastName = parts[parts.length - 1];
  }
  return next;
}

export function extractPersonNameFromShipTo(raw) {
  let text = String(raw || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (/^(attn|attention)\b/i.test(text) || /^attn\s*po\b/i.test(text)) {
    return "";
  }
  if (/\b(va\s+clinic|clinic|hospital|medical\s+center)\b/i.test(text)
    && !/^(dr\.?|doctor|mr\.?|mrs\.?|ms\.?)\b/i.test(text)) {
    return "";
  }

  text = text.replace(/\s*[-–—]\s*[A-Za-z0-9][\w./-]{2,}\s*$/g, "").trim();
  const inDept = text.match(
    /^(.+?)\s+in\s+(audiology|optometry|ophthalmology|ent|clinic|department)\b.*$/i
  );
  if (inDept) text = inDept[1].trim();

  const hasTitle = /^(dr\.?|doctor|mr\.?|mrs\.?|ms\.?|miss|prof\.?)\b/i.test(
    text
  );
  const tokens = text.split(/\s+/).filter(Boolean);
  if (!hasTitle && tokens.length < 2) return "";
  return text;
}

const CONFIRM_ORDER_ID_KEYS = [
  "shopifyId",
  "shopifyOrderId",
  "ShopifyId",
  "ShopifyOrderId",
  "shopify_id",
  "shopify_order_id",
  "orderId",
  "order_id",
  "id",
];

function pickConfirmOrderId(source) {
  if (source == null) return null;
  if (typeof source !== "object") return String(source);

  for (const key of CONFIRM_ORDER_ID_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }

  return null;
}

function unwrapConfirmPayload(response) {
  if (response == null || typeof response !== "object") return response;

  let current = response;
  for (let depth = 0; depth < 5; depth += 1) {
    const directId = pickConfirmOrderId(current);
    if (directId) return current;

    if (current.order && typeof current.order === "object") {
      const orderId = pickConfirmOrderId(current.order);
      if (orderId) return current.order;
    }

    if (current.item != null && typeof current.item === "object") {
      current = current.item;
      continue;
    }

    if (
      Object.prototype.hasOwnProperty.call(current, "data") &&
      current.data != null &&
      typeof current.data === "object" &&
      !Array.isArray(current.data)
    ) {
      current = current.data;
      continue;
    }

    break;
  }

  return current;
}

/** Strip client-only fields; send legacy prescription shape to confirm API. */
export function serializePrescriptionForApi(prescription = {}) {
  return serializeLegacyPrescription(prescription);
}

/** Build JSON or multipart body for POST /va/orders/confirm. */
export function buildConfirmOrderRequest(payload = {}) {
  const prescription = serializePrescriptionForApi(payload.prescription);
  const delivery = payload.delivery || {};
  const poNumber = String(payload.poNumber || "").trim();
  const originalPoNumber = String(payload.originalPoNumber || "").trim();
  // PO number is stored on invoicePoNumber only — not mirrored into Note.
  const orderFrom = String(payload.orderFrom || payload.OrderFrom || "").trim();
  // Same labels as Create Order UI / order details.
  const patientName = String(
    payload.patientName || payload.veteran || ""
  ).trim();
  const providerName = String(
    payload.providerName || payload.provider || ""
  ).trim();
  const baseFields = {
    parseId: payload.parseId,
    ...(Object.prototype.hasOwnProperty.call(payload, "poEmail") ? { poEmail: String(payload.poEmail || "").trim() } : {}),
    bundleCode: payload.bundleCode,
    distributorId: payload.distributorId,
    ...(payload.hospitalId ? { hospitalId: payload.hospitalId } : {}),
    ...(patientName ? { patientName } : {}),
    ...(providerName ? { providerName } : {}),
    prescription,
    delivery: {
      ...delivery,
      ...(payload.hospitalId ? { hospitalId: payload.hospitalId } : {}),
    },
    ...(payload.prescriptionFileKey
      ? { prescriptionFileKey: payload.prescriptionFileKey }
      : {}),
    ...(orderFrom
      ? {
          orderFrom,
          OrderFrom: orderFrom,
          distributorName: orderFrom,
        }
      : {}),
    ...(poNumber ? { poNumber } : {}),
    ...(originalPoNumber ? { originalPoNumber } : {}),
  };

  const hasPrescriptionFile = payload.prescriptionFile instanceof File;
  const hasOriginalPoFile = payload.originalPoFile instanceof File;

  if (hasPrescriptionFile || hasOriginalPoFile) {
    const formData = new FormData();
    formData.append("parseId", String(payload.parseId || ""));
    if (Object.prototype.hasOwnProperty.call(payload, "poEmail")) {
      formData.append("poEmail", String(payload.poEmail || "").trim());
    }
    formData.append("bundleCode", String(payload.bundleCode || ""));
    formData.append("distributorId", String(payload.distributorId || ""));
    if (payload.hospitalId) {
      formData.append("hospitalId", String(payload.hospitalId));
    }
    if (patientName) {
      formData.append("patientName", patientName);
    }
    if (providerName) {
      formData.append("providerName", providerName);
    }
    formData.append(
      "prescription",
      JSON.stringify(prescription)
    );
    formData.append(
      "delivery",
      JSON.stringify({
        ...delivery,
        ...(payload.hospitalId ? { hospitalId: payload.hospitalId } : {}),
      })
    );
    if (hasPrescriptionFile) {
      formData.append("prescriptionFile", payload.prescriptionFile);
    }
    if (payload.prescriptionFileKey) {
      formData.append("prescriptionFileKey", payload.prescriptionFileKey);
    }
    if (orderFrom) {
      formData.append("orderFrom", orderFrom);
      formData.append("OrderFrom", orderFrom);
      formData.append("distributorName", orderFrom);
    }
    if (poNumber) {
      formData.append("poNumber", poNumber);
    }
    if (originalPoNumber) {
      formData.append("originalPoNumber", originalPoNumber);
    }
    if (hasOriginalPoFile) {
      formData.append("originalPoFile", payload.originalPoFile);
    }
    return formData;
  }

  return baseFields;
}

export function normalizeConfirmResult(response) {
  const payload = unwrapConfirmPayload(response?.data ?? response?.item ?? response);
  const orderId = pickConfirmOrderId(payload);
  return {
    orderId,
    shopifyId: orderId,
    confirmationEmailStatus: payload?.confirmationEmailStatus,
    prescriptionConcerns: Array.isArray(payload?.prescriptionConcerns)
      ? payload.prescriptionConcerns : [],
  };
}

/** Shopify/order id for post-confirm navigation. */
export function getConfirmNavigationOrderId(result) {
  return result?.shopifyId || result?.orderId || null;
}

export function getVaInvoiceErrorMessage(err, { confirm = false } = {}) {
  const status = err?.status;
  const code = String(err?.data?.code || "").toUpperCase();
  const message = err?.data?.message;

  if (status === 403) return "No permission";
  if (status === 502) return "Could not read invoice, try again";
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return confirm
      ? "Could not create order, try again"
      : "Could not read invoice, try again";
  }

  if (code === "INVOICE_NOT_RECOGNIZED") {
    return (
      message ||
      "We could not find a valid VA invoice in this PDF. Please upload a clear Captify VA invoice and try again."
    );
  }

  if (code === "INVALID_QUANTITY") {
    return (
      message ||
      "Only quantity 1 is supported. Please upload an invoice for a single unit."
    );
  }

  if (code === "PRESCRIPTION_REQUIRES_LENS") {
    return (
      message ||
      "This bundle has no lens item. Remove the prescription file or values, or choose a bundle that includes a lens."
    );
  }

  if (code === "INVALID_BUNDLE_CODE") {
    return (
      message ||
      "We could not read a valid bundle code from this invoice. Please check the PDF and try again."
    );
  }

  if (code === "INVALID_PDF") {
    return (
      message ||
      "The uploaded file is not a valid PDF or could not be read."
    );
  }

  if (code === "PAYMENT_REQUIRES_ACTION") {
    return (
      message ||
      "Your bank requires OTP / card authentication to complete payment."
    );
  }

  if (status === 400 && message) return message;
  if (status === 402 && message) return message;
  return (
    message ||
    (confirm
      ? "Failed to create order. Please try again."
      : "Failed to parse invoice. Try again.")
  );
}

export function hasLowConfidence(aiMeta) {
  if (!aiMeta) return false;
  const confidence = aiMeta.confidence ?? aiMeta.score;
  if (confidence == null) return false;
  return Number(confidence) < 0.7;
}

export function formatAttnPo(poNumber) {
  return String(poNumber || "")
    .trim()
    .replace(/^(p\.?\s*o\.?\s*#?\s*)+/i, "")
    .replace(/^ATTN\s*PO:\s*/i, "")
    .trim();
}

/** Compare PO values ignoring ATTN PO: / P.O.# prefixes. */
export function normalizePoForCompare(value) {
  return formatAttnPo(value).toLowerCase();
}

export function formatCityStateZip({ city, state, zip } = {}) {
  const right = [state, zip].filter(Boolean).join(" ");
  return [city, right].filter(Boolean).join(", ");
}

export function parseCityStateZip(value) {
  const raw = String(value || "").trim();
  if (!raw) return { city: "", state: "", zip: "" };

  // "City, ST 12345" or "City, ST 12345-6789"
  const withCommaAbbr = raw.match(
    /^(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/
  );
  if (withCommaAbbr) {
    return {
      city: withCommaAbbr[1].trim(),
      state: withCommaAbbr[2].toUpperCase(),
      zip: withCommaAbbr[3],
    };
  }

  // "City, Florida 34654" (full state name)
  const withCommaFull = raw.match(
    /^(.+?),\s*([A-Za-z][A-Za-z\s]+?)\s+(\d{5}(?:-\d{4})?)\s*$/
  );
  if (withCommaFull) {
    return {
      city: withCommaFull[1].trim(),
      state: withCommaFull[2].trim(),
      zip: withCommaFull[3],
    };
  }

  // "City ST 12345"
  const withoutComma = raw.match(
    /^(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/
  );
  if (withoutComma) {
    return {
      city: withoutComma[1].trim(),
      state: withoutComma[2].toUpperCase(),
      zip: withoutComma[3],
    };
  }

  return { city: raw, state: "", zip: "" };
}

function splitRawShipToLines(rawShipTo = "") {
  return String(rawShipTo || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/g, ""))
    .filter((line) => line.trim().length > 0);
}

function isPoShipToLine(line = "") {
  return /^(ATTN\s*PO:|P\.?\s*O\.?\s*#?)/i.test(String(line || "").trim());
}

/** Prefer `ATTN PO:` for VA create/edit; keep `P.O.#` only when that was the source. */
function formatPoShipToLine(poNumber = "") {
  const raw = String(poNumber || "").trim();
  const cleaned = formatAttnPo(raw);
  if (!cleaned) return "";
  if (/^P\.?\s*O\.?\s*#?/i.test(raw) && !/^ATTN\s*PO:/i.test(raw)) {
    return formatPoNumberNote(cleaned);
  }
  return `ATTN PO: ${cleaned}`;
}

function formatPoDisplayLine(poRaw = "", defaultPo = "") {
  const cleaned = formatAttnPo(poRaw) || formatAttnPo(defaultPo);
  if (!cleaned) return "";
  if (
    /^P\.?\s*O\.?\s*#?/i.test(String(poRaw || "").trim()) &&
    !/^ATTN\s*PO:/i.test(String(poRaw || "").trim())
  ) {
    return `P.O.# ${cleaned}`;
  }
  return `ATTN PO: ${cleaned}`;
}

function looksLikeCityStateZipLine(line = "") {
  const parsed = parseCityStateZip(line);
  return Boolean(parsed.zip) || /,\s*[A-Za-z]{2}\b/.test(String(line || "").trim());
}

function looksLikePhoneLine(line = "") {
  return /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(
    String(line || "").trim()
  );
}

function isFacilityShipToLine(line = "") {
  const text = String(line || "").trim();
  if (!text || isPoShipToLine(text) || looksLikePhoneLine(text)) return false;
  if (looksLikeCityStateZipLine(text)) return false;
  return /\b(va\s+clinic|clinic|hospital|medical\s+center|vamc|outpatient)\b/i.test(
    text
  );
}

/**
 * Parse printed SHIP TO lines without assuming a fixed line count.
 * New VA creates often have hospital + clinic before ATTN PO (7+ lines);
 * older/edit saves usually have clinic + ATTN PO + recipient + street + city + phone.
 */
function parseShipToLinesToEditForm(lines = [], defaultPo = "") {
  const rows = Array.isArray(lines) ? lines.filter((l) => String(l || "").trim()) : [];
  if (!rows.length) {
    return {
      facilityName: "",
      attnPo: "",
      recipient: "",
      address1: "",
      cityStateZip: "",
      phone: "",
    };
  }

  const poIndex = rows.findIndex((line) => isPoShipToLine(line));
  const beforePo = poIndex >= 0 ? rows.slice(0, poIndex) : rows.slice(0, 1);
  const afterPo = poIndex >= 0 ? rows.slice(poIndex + 1) : rows.slice(1);

  // Prefer clinic-like line; else last non-empty line before PO (skip pure hospital when clinic exists).
  const clinicLine = [...beforePo].reverse().find((line) => isFacilityShipToLine(line));
  const facilityName = String(
    clinicLine || beforePo[beforePo.length - 1] || ""
  ).trim();

  const attnPo =
    poIndex >= 0 ? formatPoDisplayLine(rows[poIndex], defaultPo) : "";

  let idx = 0;
  const recipient = String(afterPo[idx++] || "").trim();
  const address1 = String(afterPo[idx++] || "").trim();

  let address2 = "";
  let cityStateZip = "";
  let phone = "";

  const rest = afterPo.slice(idx);
  if (rest.length) {
    const phoneIdx = rest.findIndex((line) => looksLikePhoneLine(line));
    if (phoneIdx >= 0) {
      phone = String(rest[phoneIdx] || "").trim();
      const beforePhone = rest.slice(0, phoneIdx);
      if (beforePhone.length === 1) {
        cityStateZip = String(beforePhone[0] || "").trim();
      } else if (beforePhone.length >= 2) {
        // street2 + city, or city only if first looks like city
        if (looksLikeCityStateZipLine(beforePhone[0])) {
          cityStateZip = String(beforePhone[0] || "").trim();
        } else {
          address2 = String(beforePhone[0] || "").trim();
          cityStateZip = String(beforePhone[beforePhone.length - 1] || "").trim();
        }
      }
    } else if (rest.length === 1) {
      if (looksLikeCityStateZipLine(rest[0])) {
        cityStateZip = String(rest[0] || "").trim();
      } else {
        cityStateZip = String(rest[0] || "").trim();
      }
    } else {
      address2 = String(rest[0] || "").trim();
      cityStateZip = String(rest[rest.length - 1] || "").trim();
    }
  }

  return {
    facilityName,
    attnPo,
    recipient,
    address1,
    address2,
    cityStateZip,
    ...parseCityStateZip(cityStateZip),
    phone,
  };
}

/** Map order record → delivery-style shipping edit fields. */
export function orderToShippingEditForm(order = {}) {
  const isVaOrder = isVaChannel(order);
  const rawLines = splitRawShipToLines(order.vaShipToRaw);
  const attnFromRaw = rawLines.find((line) => isPoShipToLine(line)) || "";

  // VA / structured creates: use saved order fields exactly as create/edit stored
  // them. Do not re-parse rawShipTo or invent ATTN/country from other fields.
  const hasStructuredShipping = Boolean(
    String(order.Address1 || order.ShippingStreet || "").trim() ||
      String(order.ShippingCity || "").trim() ||
      String(order.ShippingZip || "").trim() ||
      String(order.vaClinicName || "").trim() ||
      String(order.ShippingName || "").trim() ||
      String(order.ShippingPhone || "").trim()
  );

  if (hasStructuredShipping) {
    let city = String(order.ShippingCity || "").trim();
    let state = String(
      order.ShippingProvinceCode || order.ShippingProvince || ""
    ).trim();
    let zip = String(order.ShippingZip || "").trim();

    // Heal corruption: zip-only value previously saved into ShippingCity.
    if (/^\d{5}(-\d{4})?$/.test(city)) {
      if (!zip || zip === city) zip = city;
      city = "";
    }

    // Only split a legacy "City, ST ZIP" blob when province/zip were never stored.
    if (
      city &&
      !String(order.ShippingProvince || "").trim() &&
      !String(order.ShippingProvinceCode || "").trim() &&
      !zip &&
      /,\s*.+\s+\d{5}/.test(city)
    ) {
      const parsed = parseCityStateZip(city);
      city = parsed.city || city;
      state = parsed.state || state;
      zip = parsed.zip || zip;
    }

    if (/^[A-Za-z]{2}$/.test(state)) {
      state = state.toUpperCase();
    }

    return {
      facilityName: isVaOrder
        ? String(order.vaClinicName || order.clinicName || "").trim()
        : String(
            order.vaClinicName || order.clinicName || order.vaHospitalName || ""
          ).trim(),
      // ATTN only when it was saved in SHIP TO — never resurrect invoicePoNumber.
      attnPo: isVaOrder ? String(attnFromRaw || "").trim() : "",
      recipient: String(order.ShippingName || "").trim(),
      address1: String(order.Address1 || order.ShippingStreet || "").trim(),
      address2: String(order.Address2 || "").trim(),
      city,
      state,
      zip,
      country: String(order.ShippingCountry || "").trim(),
      phone: String(order.ShippingPhone || "").trim(),
    };
  }

  // Legacy fallback when structured shipping fields are missing.
  const defaultPo = formatAttnPo(order.invoicePoNumber || order.poNumber);
  const fromRaw =
    rawLines.length > 0 ? parseShipToLinesToEditForm(rawLines, "") : null;

  const city =
    String(order.ShippingCity || "").trim() ||
    parseCityStateZip(fromRaw?.cityStateZip || "").city ||
    "";
  const state =
    String(order.ShippingProvinceCode || order.ShippingProvince || "")
      .trim()
      .toUpperCase() ||
    parseCityStateZip(fromRaw?.cityStateZip || "").state ||
    "";
  const zip =
    String(order.ShippingZip || "").trim() ||
    parseCityStateZip(fromRaw?.cityStateZip || "").zip ||
    "";

  return {
    facilityName:
      String(order.vaClinicName || order.clinicName || "").trim() ||
      fromRaw?.facilityName ||
      "",
    attnPo: attnFromRaw || (defaultPo ? `ATTN PO: ${defaultPo}` : ""),
    recipient: String(
      order.ShippingName || fromRaw?.recipient || ""
    ).trim(),
    address1: String(
      order.Address1 || order.ShippingStreet || fromRaw?.address1 || ""
    ).trim(),
    address2: String(order.Address2 || fromRaw?.address2 || "").trim(),
    city,
    state,
    zip,
    country: String(
      order.ShippingCountry || order.AddressCountryCode || ""
    ).trim(),
    phone: String(order.ShippingPhone || fromRaw?.phone || "").trim(),
  };
}

/** Build order patch from delivery-style shipping edit fields. */
export function shippingEditFormToOrderPatch(
  form = {},
  order = {},
  { includeCustomerIdentity = false } = {}
) {
  // Separate city/state/zip fields are source of truth. Never fall back to
  // cityStateZip — a zip-only string like "34654" parses as city and corrupts data.
  const hasSeparateFields =
    Object.prototype.hasOwnProperty.call(form, "city") ||
    Object.prototype.hasOwnProperty.call(form, "state") ||
    Object.prototype.hasOwnProperty.call(form, "zip");

  let nextCity;
  let nextState;
  let nextZip;

  if (hasSeparateFields) {
    nextCity = String(form.city ?? "").trim();
    nextState = String(form.state ?? "").trim();
    nextZip = String(form.zip ?? "").trim();
    if (/^[A-Za-z]{2}$/.test(nextState)) {
      nextState = nextState.toUpperCase();
    }
  } else {
    const fromCombined = parseCityStateZip(form.cityStateZip);
    nextCity = fromCombined.city;
    nextState = fromCombined.state;
    nextZip = fromCombined.zip;
  }

  const nextProvinceCode = /^[A-Za-z]{2}$/.test(nextState)
    ? nextState.toUpperCase()
    : String(order.ShippingProvinceCode || "").trim();

  const attnPo = formatAttnPo(form.attnPo);
  const facility = String(form.facilityName || "").trim();
  const recipient = String(form.recipient || "").trim();
  const address1 = String(form.address1 || "").trim();
  const address2 = String(form.address2 || "").trim();
  const country = String(form.country || "").trim();
  const phone = String(form.phone || "").trim();
  const fullName = String(form.fullName || form.customerName || "").trim();
  // Only the form ATTN value — never revive order.invoicePoNumber into SHIP TO.
  const poLine = form.attnPo ? formatPoShipToLine(form.attnPo) : "";
  // Avoid duplicating PO when facility field still holds the old P.O.# line
  const facilityIsSamePo =
    isPoShipToLine(facility) && formatAttnPo(facility) === attnPo;
  const isVaOrder = isVaChannel(order);
  const patchCustomerIdentity = includeCustomerIdentity || !isVaOrder;

  // Keep city / state / zip as separate SHIP TO lines so a zip-only value
  // never gets re-parsed as a city on the next open.
  const vaShipToRaw = [
    facilityIsSamePo ? "" : facility,
    poLine,
    recipient,
    address1,
    address2,
    nextCity,
    nextState,
    nextZip,
    country,
    phone,
  ]
    .filter(Boolean)
    .join("\n");

  return {
    // VA default: Name / Email stay as distributor — admins can overwrite.
    ...(patchCustomerIdentity
      ? {
          customerName: fullName,
          customerEmail: form.email,
        }
      : {}),
    // Exact recipient from the form (allow empty for VA).
    ShippingName: isVaOrder
      ? recipient
      : recipient || fullName,
    Address1: address1,
    Address2: address2,
    ShippingCity: nextCity,
    ShippingProvince: nextState,
    ...(nextProvinceCode ? { ShippingProvinceCode: nextProvinceCode } : {}),
    ShippingZip: nextZip,
    ShippingCountry: country,
    ShippingPhone: phone,
    // Clinic / ATTN PO / SHIP TO block are VA invoice fields only.
    ...(isVaOrder
      ? {
          // Only update order-level PO when ATTN PO is present — clearing the
          // shipping ATTN line must not wipe invoicePoNumber.
          ...(poLine || attnPo ? { invoicePoNumber: poLine || attnPo } : {}),
          // Order model field is vaClinicName (not clinicName).
          vaClinicName: facilityIsSamePo ? "" : facility,
          vaShipToRaw,
        }
      : {}),
  };
}

export function splitFullName(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

/** Map order record → audiologist Confirmation delivery fields. */
export function orderToAudiologistDeliveryForm(order = {}) {
  const fromShipping = splitFullName(order.ShippingName);
  const fromCustomer = splitFullName(order.customerName);
  const firstName = fromShipping.firstName || fromCustomer.firstName;
  const lastName =
    fromShipping.lastName ||
    (fromShipping.firstName ? "" : fromCustomer.lastName);

  return {
    firstName,
    lastName,
    address1: String(order.Address1 || order.ShippingStreet || "").trim(),
    address2: String(order.Address2 || "").trim(),
    city: String(order.ShippingCity || "").trim(),
    state: String(order.ShippingProvince || "").trim(),
    zip: String(order.ShippingZip || "").trim(),
    phone: String(order.ShippingPhone || "").trim(),
    email: String(order.customerEmail || "").trim(),
    shippingEmail: String(order.shippingEmail || "").trim(),
  };
}

/** Map audiologist delivery form → order fields. */
export function audiologistDeliveryToOrderPatch(
  form = {},
  { includeCustomerIdentity = true } = {}
) {
  const firstName = String(form.firstName || "").trim();
  const lastName = String(form.lastName || "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const contactEmail = String(form.email || "").trim();
  const deliveryEmail = String(form.shippingEmail || "").trim().toLowerCase();

  return {
    ...(includeCustomerIdentity
      ? {
          customerName: fullName,
          customerEmail: contactEmail,
        }
      : {}),
    ShippingName: fullName,
    Address1: String(form.address1 || "").trim(),
    Address2: String(form.address2 || "").trim(),
    ShippingCity: String(form.city || "").trim(),
    ShippingProvince: String(form.state || "").trim(),
    ShippingZip: String(form.zip || "").trim(),
    ShippingPhone: String(form.phone || "").trim(),
    shippingEmail: deliveryEmail,
  };
}

/** Map Shopify / bundle productType → Item Ordered dropdown value. */
export function normalizeShopifyProductType(productType) {
  const raw = String(productType || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "frame" || raw === "glass" || raw === "glasses") return "frame";
  if (raw === "lens" || raw === "lenses") return "lens";
  if (raw === "sunglasses" || raw === "sunglass") return "sunglasses";
  if (raw === "accessory" || raw === "accessories") return "accessories";
  if (raw === "services" || raw === "service") return "services";
  if (raw === "lensadvisor" || raw === "lensadvizor") return "lens";
  return null;
}

function normalizeStoredProductType(productType) {
  if (!productType) return null;
  const normalized = String(productType).toLowerCase().trim();
  if (!normalized) return null;
  if (normalized === "lensadvizor" || normalized === "lensadvisor") {
    return "lens";
  }
  return normalized;
}

/** True for lens, lensadvisor, and lensadvizor product types. */
export function isLensProductType(productType) {
  const raw = String(productType || "")
    .trim()
    .toLowerCase();
  return (
    raw === "lens" ||
    raw === "lenses" ||
    raw === "lensadvisor" ||
    raw === "lensadvizor"
  );
}

/**
 * GET-API / UI ProductType overwrite (matches backend productType.helper):
 * - frame → frame
 * - lens / lensadvisor → lens
 * - any other non-empty → other
 * - missing / empty → unknown
 */
export function overwriteProductTypeForApi(productType) {
  const key = String(productType || "")
    .trim()
    .toLowerCase();
  if (!key) return "unknown";
  if (key === "frame" || key === "frames") return "frame";
  if (
    key === "lens" ||
    key === "lenses" ||
    key === "lensadvizor" ||
    key === "lensadvisor"
  ) {
    return "lens";
  }
  if (key === "other" || key === "unknown") return key;
  return "other";
}

/** Item Ordered dropdown value — same overwrite as GET APIs. */
export function displayProductTypeValue(productType) {
  return overwriteProductTypeForApi(productType);
}

export function isUnknownProductType(productType) {
  return overwriteProductTypeForApi(productType) === "unknown";
}

export function isFrameProductType(productType) {
  return overwriteProductTypeForApi(productType) === "frame";
}

export function isOtherProductType(productType) {
  return overwriteProductTypeForApi(productType) === "other";
}

function inferProductTypeFromSkuName(skuName = "") {
  const sku = String(skuName || "").toLowerCase();
  if (!sku) return null;
  if (sku.includes("sunglass")) return "sunglasses";
  if (sku.includes("lensadvisor") || sku.includes("lensadvizor")) {
    return "lensadvisor";
  }
  if (/\blens(es)?\b/.test(sku) && !sku.includes("sunglass")) return "lens";
  if (sku.includes("glasses") || sku.includes(" frame")) return "frame";
  if (
    sku.includes("warranty") ||
    sku.includes("premium")
  ) {
    return "services";
  }
  if (
    sku.includes("charging") ||
    sku.includes("cable") ||
    sku.includes("microphone") ||
    sku.includes("accessory")
  ) {
    return "accessories";
  }
  return null;
}

/** Effective Item Ordered type for UI: stored / Shopify type only. No title/SKU guesses. */
export function resolveOrderItemProductType(order = {}) {
  if (
    order?.ProductType !== null &&
    order?.ProductType !== undefined &&
    String(order.ProductType).trim() !== ""
  ) {
    return overwriteProductTypeForApi(order.ProductType);
  }

  const shopifyRaw =
    order?.shopifyProductType ??
    order?.ShopifyProductType ??
    order?.shopify?.productType ??
    order?.productType;
  const fromShopify = normalizeShopifyProductType(shopifyRaw);
  if (fromShopify) return overwriteProductTypeForApi(fromShopify);

  const channel = String(order?.channel || "").toLowerCase();
  if (channel === "va" || channel === "audiologist") {
    return "unknown";
  }

  const fromSku = inferProductTypeFromSkuName(order?.SkuName);
  if (fromSku) return overwriteProductTypeForApi(fromSku);

  return "unknown";
}

function formatBundleProductTypeLabel(rawType) {
  const raw = String(rawType || "").trim();
  const lower = raw.toLowerCase();
  if (!raw || lower === "unknown") return "Unknown";
  if (lower === "accessory") return "Accessory";
  if (lower === "accessories") return "Accessories";
  if (lower === "frame") return "Frame";
  if (lower === "sunglasses" || lower === "sunglass") return "Sunglasses";
  if (
    lower === "lens" ||
    lower === "lenses" ||
    lower === "lensadvisor" ||
    lower === "lensadvizor"
  ) {
    return "Lens";
  }
  if (lower === "services" || lower === "service") return "Services";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

const BUNDLE_ITEM_TYPE_ORDER = [
  "Frame",
  "Sunglasses",
  "Lens",
  "Accessory",
  "Accessories",
  "Services",
  "Handheld Device",
  "Unknown",
];

function sortBundleTypeGroups(groups) {
  return groups.sort((a, b) => {
    const ai = BUNDLE_ITEM_TYPE_ORDER.indexOf(a.label);
    const bi = BUNDLE_ITEM_TYPE_ORDER.indexOf(b.label);
    if (ai === -1 && bi === -1) return a.label.localeCompare(b.label);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/**
 * Shopify product.productType only. Missing / blank → Unknown.
 * Do not guess from title, SKU, or VA category buckets.
 */
function inferBundleDisplayProductType(item = {}) {
  const raw = item?.productType || item?.shopify?.productType || "";
  return formatBundleProductTypeLabel(raw);
}

/** Group bundle line items by Shopify productType, keeping each name. */
export function groupBundleItemsByProductType(items = []) {
  if (!Array.isArray(items) || !items.length) return [];

  const groups = new Map();

  items.forEach((item) => {
    const label = inferBundleDisplayProductType(item);
    const name = item?.label || item?.name || item?.shopify?.title;
    if (!name) return;
    const qty = Number(item.quantity) || 1;
    const text = qty > 1 ? `${name} (x${qty})` : name;

    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(text);
  });

  return sortBundleTypeGroups(
    Array.from(groups.entries()).map(([label, names]) => ({
      key: label.toLowerCase().replace(/\s+/g, "-"),
      label,
      items: names,
    }))
  );
}

/** Group bundle line items by Shopify productType for create-order review UI. */
export function bundleItemsToProductTypeRows(items = []) {
  return groupBundleItemsByProductType(items).map((group) => ({
    key: group.key,
    label: group.label,
    included: true,
    text: group.items.join(", "),
  }));
}

/** Format one bundleProducts section for display. */
export function formatBundleProductSection(section) {
  if (!section) return "—";
  if (section.included === false) return "None";

  if (section.description && String(section.description).trim()) {
    return String(section.description).trim();
  }

  if (Array.isArray(section.items) && section.items.length) {
    return section.items.filter(Boolean).join(", ");
  }

  if (section.details && String(section.details).trim()) {
    return String(section.details).trim();
  }

  if (section.quantity != null && section.quantity !== "") {
    return `Qty: ${section.quantity}`;
  }

  return section.included ? "Included" : "None";
}

const BUNDLE_PRODUCT_ORDER = [
  "glasses",
  "accessories",
  "handheldDevice",
  "handheld",
  "lenses",
];

function humanizeBundleProductKey(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Turn API bundleProducts into ordered rows for the review UI. */
export function normalizeBundleProducts(bundleProducts) {
  if (!bundleProducts || typeof bundleProducts !== "object") return [];

  return Object.entries(bundleProducts)
    .map(([key, section]) => ({
      key,
      label: section?.label || humanizeBundleProductKey(key),
      included: section?.included !== false,
      text: formatBundleProductSection(section),
    }))
    .sort((a, b) => {
      const ai = BUNDLE_PRODUCT_ORDER.indexOf(a.key);
      const bi = BUNDLE_PRODUCT_ORDER.indexOf(b.key);
      if (ai === -1 && bi === -1) return a.label.localeCompare(b.label);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
}

/** Legacy flat breakdown → product rows (audiologist / mock fallback). */
export function legacyBreakdownToProductRows(breakdown = {}) {
  return [
    { key: "glasses", label: "Glasses", included: breakdown.glasses !== "None", text: breakdown.glasses || "None" },
    { key: "accessories", label: "Accessories", included: breakdown.accessories !== "None", text: breakdown.accessories || "None" },
    { key: "handheld", label: "Handheld Device", included: breakdown.handheld !== "None", text: breakdown.handheld || "None" },
    { key: "lenses", label: "Lenses", included: breakdown.lenses !== "None", text: breakdown.lenses || "None" },
  ];
}

function normalizeBundleDetailItems(items, fallbackValue) {
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => String(item || "").trim()).filter(Boolean);
  }
  const value = String(fallbackValue ?? "").trim();
  if (!value || ["none", "n/a", "—", "-"].includes(value.toLowerCase())) {
    return [];
  }
  return [value];
}

/**
 * Audiologist bundle card / confirm rows.
 * Shopify productType split (Frame, Sunglasses, Accessory, Services).
 * Missing Shopify type → Unknown — never guessed from title.
 */
export function getAudiologistBundleDetailRows(bundle = {}) {
  const typeGroups = groupBundleItemsByProductType(bundle?.items);
  if (typeGroups.length) {
    return typeGroups.map((group) => ({
      label: group.label,
      items: group.items,
    }));
  }

  const leftover = [
    ...normalizeBundleDetailItems(bundle.glassesItems, bundle.glasses),
    ...normalizeBundleDetailItems(bundle.sunglassesItems, bundle.sunglasses),
    ...normalizeBundleDetailItems(bundle.accessoriesItems, bundle.accessories),
    ...normalizeBundleDetailItems(bundle.servicesItems, bundle.services),
    ...normalizeBundleDetailItems(bundle.handheldItems, bundle.handheld),
    ...normalizeBundleDetailItems(bundle.lensesItems, bundle.lenses),
  ];

  if (!leftover.length) return [];
  return [{ label: "Unknown", items: leftover }];
}

/** Map GET /va/bundles item rows to breakdown labels for the review UI. */
export function bundleItemsToBreakdown(items = []) {
  const buckets = {
    glasses: [],
    accessories: [],
    handheld: [],
    lenses: [],
  };

  items.forEach((item) => {
    const rawType = String(
      item?.type || item?.category || item?.productType || ""
    ).toLowerCase();
    const label = item?.label || item?.name || item?.shopify?.title || null;
    if (!label) return;

    let key = "accessories";
    if (
      rawType === "glasses" ||
      rawType === "frame" ||
      rawType === "glass"
    ) {
      key = "glasses";
    } else if (
      rawType === "lenses" ||
      rawType === "lens"
    ) {
      key = "lenses";
    } else if (
      rawType === "handheld" ||
      rawType === "handhelddevice" ||
      rawType === "handheld_device"
    ) {
      key = "handheld";
    } else if (
      rawType === "accessories" ||
      rawType === "accessory"
    ) {
      key = "accessories";
    }

    const qty = Number(item.quantity) || 1;
    buckets[key].push(qty > 1 ? `${label} (x${qty})` : label);
  });

  const joinOrNone = (list) => (list.length ? list.join(", ") : "None");

  return {
    glasses: joinOrNone(buckets.glasses),
    accessories: joinOrNone(buckets.accessories),
    handheld: joinOrNone(buckets.handheld),
    lenses: joinOrNone(buckets.lenses),
  };
}

export function normalizeBundlesResponse(response) {
  let payload = response?.data ?? response;
  // Handle { success, data: { bundles } } and double-wrapped payloads
  if (payload?.data && !payload?.bundles) {
    payload = payload.data;
  }

  const raw =
    payload?.bundles ??
    payload?.items ??
    (Array.isArray(payload) ? payload : []);

  return (Array.isArray(raw) ? raw : []).map((bundle) => {
    const items = Array.isArray(bundle.items) ? bundle.items : [];
    const bundleProducts =
      bundle.bundleProducts ??
      bundle.bundle_products ??
      null;

    let productRows = bundleProducts
      ? normalizeBundleProducts(bundleProducts)
      : [];

    if (items.length) {
      const typeRows = bundleItemsToProductTypeRows(items);
      if (typeRows.length) {
        productRows = typeRows;
      } else if (
        !productRows.length ||
        productRows.every((r) => r.text === "None")
      ) {
        productRows = legacyBreakdownToProductRows(bundleItemsToBreakdown(items));
      }
    }

    return {
      code: bundle.code,
      label: bundle.label || bundle.code,
      mapped: bundle.mapped,
      items,
      bundleProducts,
      productRows,
    };
  });
}

/** Normalize GET /va/bundles/:code → single bundle object. */
export function normalizeSingleBundleResponse(response) {
  let payload = response?.data ?? response;
  if (payload?.data && !payload?.bundle && !payload?.code) {
    payload = payload.data;
  }

  const bundle = payload?.bundle ?? payload;
  if (!bundle || typeof bundle !== "object") {
    return null;
  }

  const items = Array.isArray(bundle.items) ? bundle.items : [];
  const bundleProducts =
    bundle.bundleProducts ?? bundle.bundle_products ?? null;

  let productRows = bundleProducts
    ? normalizeBundleProducts(bundleProducts)
    : [];

  if (items.length) {
    const typeRows = bundleItemsToProductTypeRows(items);
    if (typeRows.length) {
      productRows = typeRows;
    } else if (
      !productRows.length ||
      productRows.every((r) => r.text === "None")
    ) {
      productRows = legacyBreakdownToProductRows(bundleItemsToBreakdown(items));
    }
  }

  return {
    code: bundle.code,
    label: bundle.label || bundle.code,
    mapped: bundle.mapped,
    items,
    bundleProducts,
    productRows,
  };
}

/** VA invoice / audiologist create-order channels (not legacy Shopify). */
export function isVaOrAudiologistChannel(order) {
  return isVaChannel(order) || isAudiologistChannel(order);
}

function getOrderTags(order) {
  return [
    ...(Array.isArray(order?.Tag)
      ? order.Tag
      : String(order?.Tag || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)),
    ...(Array.isArray(order?.productTag) ? order.productTag : []),
  ].map((tag) =>
    String(tag || "")
      .trim()
      .toLowerCase()
  );
}

/** Audiologist create-order channel (AUD bundles), not VA invoice / Shopify. */
export function isAudiologistChannel(order) {
  const bundle = getOrderBundleCode(order);
  if (/^AUD/i.test(bundle)) return true;

  const channel = String(order?.channel || "")
    .trim()
    .toLowerCase();
  if (channel === "audiologist") return true;
  if (channel === "va") return false;

  return getOrderTags(order).some((tag) => tag === "audiologist");
}

/** VA invoice / Lovell channel (not audiologist, not legacy Shopify). */
export function isVaChannel(order) {
  if (isAudiologistChannel(order)) return false;

  const channel = String(order?.channel || "")
    .trim()
    .toLowerCase();
  if (channel === "va") return true;

  return getOrderTags(order).some((tag) => tag === "va" || tag === "lovell");
}

const PRODUCTION_PROVIDER_CODES = new Set(["ARTS", "PP", "YUFENG"]);

function isProductionProviderCode(value) {
  return PRODUCTION_PROVIDER_CODES.has(
    String(value || "")
      .trim()
      .toUpperCase()
  );
}

/**
 * Resolve VA "Provider name" (clinician) from an order / line item.
 * Prefer providerName / vaAudiologistName (Create Order modal); ignore
 * production supplier codes on `provider` (ARTS / PP / YUFENG).
 */
export function resolveVaProviderName(order) {
  if (!order || typeof order !== "object") return "";

  const candidates = [
    order.providerName,
    order.ProviderName,
    order.vaProviderName,
    // Create Order confirm also mirrors clinician here.
    order.vaAudiologistName,
    order.invoiceMeta?.providerName,
    order.aiParseMeta?.providerName,
    order.aiParseMeta?.invoiceMeta?.providerName,
    // Legacy aliases (older confirms / extract).
    order.invoiceMeta?.provider,
    order.aiParseMeta?.provider,
    order.aiParseMeta?.invoiceMeta?.provider,
    // Last: top-level provider, but skip ARTS/PP/YUFENG production codes.
    isProductionProviderCode(order.provider) ? "" : order.provider,
    isProductionProviderCode(order.Provider) ? "" : order.Provider,
  ];

  return (
    candidates
      .map((value) => String(value || "").trim())
      .find(Boolean) || ""
  );
}

export function normalizeBundleCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "");
}

/** CAP2 → CAP-2, AUD1A → AUD-1A (order details / notes display). */
export function formatBundleDisplayLabel(code) {
  const raw = normalizeBundleCode(code);
  if (!raw) return "";
  const match = raw.match(/^(CAP|AUD)(\d+)([A-Z]?)$/i);
  if (!match) return String(code || "").trim();
  return `${match[1].toUpperCase()}-${match[2]}${(match[3] || "").toUpperCase()}`;
}

/** CAP1 / CAP2A / AUD1 from bundleCode, or CAP/AUD order tags. */
export function getOrderBundleCode(order) {
  const direct = normalizeBundleCode(order?.bundleCode || order?.BundleCode);
  if (direct) return direct;

  const tags = [
    ...(Array.isArray(order?.Tag)
      ? order.Tag
      : String(order?.Tag || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)),
    ...(Array.isArray(order?.productTag) ? order.productTag : []),
  ];

  for (const tag of tags) {
    const normalized = normalizeBundleCode(tag);
    if (/^(CAP|AUD)\d+[A-Z]?$/i.test(normalized)) return normalized;
  }
  return "";
}

/** Sort by ItemID suffix numerically: CP+35385-1, -2, -3 … (not string order -10 before -7). */
function compareBundleLineItemIds(a = {}, b = {}) {
  const parseSuffix = (item) => {
    const match = String(item?.ItemID || "").match(/-(\d+)\s*$/);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  };
  const sa = parseSuffix(a);
  const sb = parseSuffix(b);
  if (sa !== sb) return sa - sb;
  return String(a?.ItemID || "").localeCompare(String(b?.ItemID || ""));
}

function sortBundleGroupIndices(orders = [], indices = []) {
  return [...indices].sort((ia, ib) =>
    compareBundleLineItemIds(orders[ia] || {}, orders[ib] || {})
  );
}

/** CAP1 before CAP2. */
function parseBundleCodeSortParts(code = "") {
  const raw = normalizeBundleCode(code);
  const match = raw.match(/^(CAP|AUD)(\d+)([A-Z]?)$/i);
  if (!match) {
    return {
      family: 99,
      num: Number.MAX_SAFE_INTEGER,
      suffix: raw,
    };
  }
  return {
    family: String(match[1]).toUpperCase() === "CAP" ? 0 : 1,
    num: Number(match[2]),
    suffix: String(match[3] || "").toUpperCase(),
  };
}

function compareBundleGroups(a = {}, b = {}) {
  const pa = parseBundleCodeSortParts(a.code);
  const pb = parseBundleCodeSortParts(b.code);
  if (pa.family !== pb.family) return pa.family - pb.family;
  if (pa.num !== pb.num) return pa.num - pb.num;
  if (pa.suffix !== pb.suffix) return pa.suffix.localeCompare(pb.suffix);

  const firstA = a.items?.[0] || {};
  const firstB = b.items?.[0] || {};
  return compareBundleLineItemIds(firstA, firstB);
}

/**
 * Display ItemID for multi-unit bundles: CP+34941-1A, CP+34941-1B …
 * (unit letter + 1-based position within that unit).
 */
export function formatBundleUnitItemDisplayId(
  item = {},
  unitIndex = 0,
  positionInUnit = 0,
  unitTotal = 0
) {
  const raw = String(item?.ItemID || "").trim();
  if (!raw) return "";
  if (!(unitTotal > 1) || !(unitIndex > 0) || !(positionInUnit > 0)) {
    return raw;
  }
  const match = raw.match(/^(CP\+[^-]+)-\d+$/i);
  if (!match) return raw;
  const letter = String.fromCharCode(64 + Math.min(unitIndex, 26));
  return `${match[1]}-${positionInUnit}${letter}`;
}

/**
 * Group order line rows by bundle code for VA/audiologist order details.
 * Qty > 1 (demoUnitIndex): one sidebar group (CAP-1 + badge), units CAP-1-1 / CAP-1-2 in main.
 * Lines without a code stay as their own group labeled by ItemID.
 */
export function groupOrderItemsByBundle(orders = []) {
  const groups = [];
  const byKey = new Map();

  (orders || []).forEach((order, index) => {
    const code = getOrderBundleCode(order);
    const unitIndex = Math.max(0, Number(order?.demoUnitIndex) || 0);
    const unitTotal = Math.max(0, Number(order?.demoBundleQuantity) || 0);
    const lineQty = Math.max(1, Number(order?.Quantity) || 1);
    // One sidebar entry per bundle code (units nest underneath).
    const key = code || `ITEM:${order?.ItemID || index}`;

    if (!byKey.has(key)) {
      const displayCode = formatBundleDisplayLabel(code);
      const group = {
        key,
        code,
        displayCode: displayCode || "",
        // Sidebar: CAP-1 (qty badge rendered separately when quantity > 1)
        label: displayCode || order?.ItemID || `Item ${index + 1}`,
        quantity: 1,
        unitTotal: 0,
        indices: [],
        items: [],
        units: [],
        _unitsByIndex: new Map(),
      };
      byKey.set(key, group);
      groups.push(group);
    }

    const group = byKey.get(key);
    group.indices.push(index);
    group.items.push(order);

    const effectiveUnit = unitIndex > 0 ? unitIndex : 1;
    const effectiveTotal =
      unitTotal > 1 ? unitTotal : Math.max(1, Number(group.quantity) || 1);

    if (!group._unitsByIndex.has(effectiveUnit)) {
      const displayCode =
        group.displayCode || formatBundleDisplayLabel(code) || group.label;
      const unitLabel =
        effectiveTotal > 1
          ? `Bundle: ${displayCode}-${effectiveUnit}`
          : `Bundle: ${displayCode}`;
      const unit = {
        key: `${key}::U${effectiveUnit}`,
        unitIndex: effectiveUnit,
        unitTotal: effectiveTotal,
        label: unitLabel,
        indices: [],
        items: [],
      };
      group._unitsByIndex.set(effectiveUnit, unit);
      group.units.push(unit);
    }

    const unit = group._unitsByIndex.get(effectiveUnit);
    unit.indices.push(index);
    unit.items.push(order);
    if (unitTotal > group.unitTotal) group.unitTotal = unitTotal;
    if (unitTotal > group.quantity) group.quantity = unitTotal;
    if (lineQty > group.quantity && !(unitTotal > 1)) {
      group.quantity = lineQty;
    }
  });

  for (const group of groups) {
    group.indices = sortBundleGroupIndices(orders, group.indices);
    group.items = group.indices.map((index) => orders[index]);

    group.units.sort(
      (a, b) => (Number(a.unitIndex) || 0) - (Number(b.unitIndex) || 0)
    );
    for (const unit of group.units) {
      unit.indices = sortBundleGroupIndices(orders, unit.indices);
      unit.items = unit.indices.map((index) => orders[index]);
      const total = Math.max(
        Number(group.unitTotal) || 0,
        Number(group.quantity) || 1,
        unit.unitTotal || 1
      );
      unit.unitTotal = total;
      if (group.displayCode && total > 1) {
        unit.label = `Bundle: ${group.displayCode}-${unit.unitIndex}`;
      }
    }

    if (group.units.length > group.quantity) {
      group.quantity = group.units.length;
    }
    delete group._unitsByIndex;
  }

  groups.sort(compareBundleGroups);
  return groups;
}

/** Captify Premium line (shows redemption badge on order details). */
export function isCaptifyPremiumOrderLine(order) {
  const haystack = [order?.SkuName, order?.ItemName, order?.title, order?.ProductType]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes("captify premium");
}

/** Prefer line code, else any sibling row on the same Shopify order. */
export function resolveOrderRedemptionCode(order, siblingOrders = []) {
  const direct = String(order?.redemptionCode || "").trim();
  if (direct) return direct;
  const shopifyId = order?.ShopifyId != null ? String(order.ShopifyId) : "";
  for (const row of siblingOrders || []) {
    if (shopifyId && row?.ShopifyId != null && String(row.ShopifyId) !== shopifyId) {
      continue;
    }
    const code = String(row?.redemptionCode || "").trim();
    if (code) return code;
  }
  return "";
}

/** Prefer frame, then lens, then first line in a bundle group. */
export function preferBundlePrimaryIndex(orders = [], indices = []) {
  if (!indices.length) return 0;
  const frameIdx = indices.find((i) =>
    isFrameProductType(resolveOrderItemProductType(orders[i]))
  );
  if (frameIdx != null) return frameIdx;
  const lensIdx = indices.find((i) =>
    isLensProductType(resolveOrderItemProductType(orders[i]))
  );
  if (lensIdx != null) return lensIdx;
  return indices[0];
}

/** Build CAP* included-product rows from persisted order line items. */
export function orderItemsToBundleDetailRows(orders = []) {
  return bundleItemsToProductTypeRows(
    (orders || []).map((order) => ({
      productType: resolveOrderItemProductType(order),
      name: order?.SkuName || order?.ItemID || "Item",
      quantity: 1,
    }))
  );
}
